import React, { useState } from 'react';
import { XIcon, CheckIcon, SearchIcon, UsersIcon, PlusIcon, Loader2Icon } from 'lucide-react';
type CreateGroupProps = {
  onClose: () => void;
  onSave: (group: {
    id: string;
    name: string;
    avatar: string;
    members: string[];
  }) => void;
};
const GROUP_AVATARS = ['👥', '🌟', '🎨', '🎮', '📚', '🎭', '🎪', '🎡'];
type FriendSuggestion = {
  id: string;
  name: string;
  avatar: string;
  type: 'friend' | 'parent';
};
export const CreateGroup = ({
  onClose,
  onSave
}: CreateGroupProps) => {
  const [groupName, setGroupName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Mock data - In real app, this would come from a database
  const suggestions: FriendSuggestion[] = [{
    id: '1',
    name: "Sarah's Parent",
    avatar: '👩',
    type: 'parent'
  }, {
    id: '2',
    name: 'Tommy',
    avatar: '🦁',
    type: 'friend'
  }, {
    id: '3',
    name: 'Lucy',
    avatar: '🦄',
    type: 'friend'
  }, {
    id: '4',
    name: "Alex's Parent",
    avatar: '👨',
    type: 'parent'
  }, {
    id: '5',
    name: 'Maya',
    avatar: '🐼',
    type: 'friend'
  }];
  const filteredSuggestions = suggestions.filter(friend => friend.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const toggleMember = (friendId: string) => {
    setSelectedMembers(prev => prev.includes(friendId) ? prev.filter(id => id !== friendId) : [...prev, friendId]);
  };
  const handleCreate = () => {
    setIsCreating(true);
    setTimeout(() => {
      onSave({
        id: Date.now().toString(),
        name: groupName,
        avatar: selectedAvatar,
        members: selectedMembers
      });
    }, 1000);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-357">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" data-id="element-358">
        <div className="flex items-center justify-between mb-6" data-id="element-359">
          <h2 className="text-xl font-bold text-purple-700" data-id="element-360">
            Create Story Group
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-purple-100 rounded-full transition-colors" data-id="element-361">
            <XIcon size={20} className="text-purple-700" data-id="element-362" />
          </button>
        </div>
        <div className="space-y-6" data-id="element-363">
          {/* Group Name Input */}
          <div data-id="element-364">
            <label className="block text-purple-700 font-medium mb-2" data-id="element-365">
              Group Name
            </label>
            <input type="text" value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Enter group name..." className="w-full px-4 py-2 border border-purple-200 rounded-full focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100" data-id="element-366" />
          </div>
          {/* Avatar Selection */}
          <div data-id="element-367">
            <label className="block text-purple-700 font-medium mb-2" data-id="element-368">
              Choose Group Avatar
            </label>
            <div className="grid grid-cols-4 gap-4" data-id="element-369">
              {GROUP_AVATARS.map(avatar => <button key={avatar} onClick={() => setSelectedAvatar(avatar)} className={`aspect-square text-3xl rounded-xl border-2 transition-all ${selectedAvatar === avatar ? 'border-purple-600 bg-purple-50 scale-110' : 'border-purple-200 hover:border-purple-300'}`} data-id="element-370">
                  {avatar}
                </button>)}
            </div>
          </div>
          {/* Member Selection */}
          <div data-id="element-371">
            <label className="block text-purple-700 font-medium mb-2" data-id="element-372">
              Add Members
            </label>
            <div className="relative mb-4" data-id="element-373">
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search friends..." className="w-full pl-10 pr-4 py-2 border border-purple-200 rounded-full focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100" data-id="element-374" />
              <SearchIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400" data-id="element-375" />
            </div>
            <div className="max-h-48 overflow-y-auto" data-id="element-376">
              {filteredSuggestions.map(friend => <button key={friend.id} onClick={() => toggleMember(friend.id)} className={`w-full flex items-center p-3 rounded-xl mb-2 transition-colors ${selectedMembers.includes(friend.id) ? 'bg-purple-100 border-2 border-purple-300' : 'bg-purple-50 hover:bg-purple-100'}`} data-id="element-377">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-2xl" data-id="element-378">
                    {friend.avatar}
                  </div>
                  <div className="ml-3 flex-grow text-left" data-id="element-379">
                    <div className="font-medium text-purple-700" data-id="element-380">
                      {friend.name}
                    </div>
                    <div className="text-sm text-purple-500" data-id="element-381">
                      {friend.type === 'parent' ? 'Parent' : 'Friend'}
                    </div>
                  </div>
                  {selectedMembers.includes(friend.id) && <CheckIcon size={20} className="text-purple-600" data-id="element-382" />}
                </button>)}
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-purple-100" data-id="element-383">
            <button onClick={onClose} className="px-4 py-2 rounded-full border-2 border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors" data-id="element-384">
              Cancel
            </button>
            <button onClick={handleCreate} disabled={!groupName || !selectedAvatar || selectedMembers.length === 0 || isCreating} className="px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-385">
              {isCreating ? <>
                  <Loader2Icon size={20} className="animate-spin mr-2" data-id="element-386" />
                  Creating...
                </> : <>
                  <PlusIcon size={20} className="mr-2" data-id="element-387" />
                  Create Group
                </>}
            </button>
          </div>
        </div>
      </div>
    </div>;
};